import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { UserPlus, KeyRound, Loader2, Mail, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errText } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const genPw = () => `Isgs-${Math.random().toString(36).slice(2, 8)}${Math.floor(10 + Math.random() * 89)}`;

const AddDialog = ({ open, setOpen }) => {
  const qc = useQueryClient();
  const [f, setF] = useState({ name: "", email: "", mobile: "", role: "counsellor", password: genPw() });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/users", f);
      qc.invalidateQueries({ queryKey: ["users"] });
      toast.success(`Account created. Share the temporary password with ${f.name}: ${f.password}`, { duration: 15000 });
      setOpen(false);
      setF({ name: "", email: "", mobile: "", role: "counsellor", password: genPw() });
    } catch (e) { toast.error(errText(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md" data-testid="add-staff-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Add staff account</DialogTitle>
          <DialogDescription>They will be asked to change the temporary password on first sign in.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {[["name", "Full name", "text"], ["email", "Email", "email"], ["mobile", "Mobile number", "tel"], ["password", "Temporary password", "text"]].map(([k, l, t]) => (
            <div key={k} className="space-y-1.5">
              <Label className="text-xs text-slate-600">{l}</Label>
              <Input type={t} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`staff-${k}-input`} />
            </div>
          ))}
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-600">Role</Label>
            <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
              <SelectTrigger data-testid="staff-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="counsellor">Counsellor</SelectItem>
                <SelectItem value="accountant">Accountant</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button disabled={busy || !f.name.trim() || !f.email.trim() || f.mobile.replace(/\D/g, "").length < 10 || f.password.length < 8} onClick={submit} className="bg-plum-700 hover:bg-plum-800" data-testid="staff-create-btn">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Create account
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Staff() {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const [open, setOpen] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [linkFor, setLinkFor] = useState(null);
  const [delFor, setDelFor] = useState(null);
  const { data = [] } = useQuery({ queryKey: ["users"], queryFn: () => api.get("/users").then((r) => r.data) });

  const patch = async (u, body, msg) => {
    try {
      await api.patch(`/users/${u.id}`, body);
      qc.invalidateQueries({ queryKey: ["users"] });
      toast.success(msg, { duration: msg.includes("password") ? 15000 : 4000 });
      return true;
    } catch (e) { toast.error(errText(e)); return false; }
  };
  const sendLink = async () => {
    const u = linkFor;
    setLinkFor(null);
    try {
      await api.post(`/users/${u.id}/send-reset`);
      qc.invalidateQueries({ queryKey: ["users"] });
      toast.success(`Reset link emailed to ${u.email}. Their old password no longer works.`, { duration: 6000 });
    } catch (e) { toast.error(errText(e)); }
  };
  const remove = async () => {
    const u = delFor;
    setDelFor(null);
    try {
      await api.delete(`/users/${u.id}`);
      qc.invalidateQueries({ queryKey: ["users"] });
      toast.success(`${u.name}'s account was deleted`);
    } catch (e) { toast.error(errText(e)); }
  };
  const reset = (u) => { const pw = genPw(); patch(u, { password: pw }, `New temporary password for ${u.name}: ${pw}`); };

  return (
    <div className="max-w-7xl">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <div className="fade-up">
          <div className="text-xs uppercase tracking-[0.2em] text-plum-700">{data.filter((u) => u.active).length} active accounts</div>
          <h1 className="font-display mt-0.5 text-3xl font-bold leading-tight tracking-tight text-plum-950 sm:text-4xl">Staff</h1>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-600"><Switch checked={showInactive} onCheckedChange={setShowInactive} data-testid="staff-show-inactive" />Show inactive</label>
        <Button onClick={() => setOpen(true)} className="bg-plum-700 hover:bg-plum-800" data-testid="add-staff-btn"><UserPlus className="mr-2 h-4 w-4" />Add staff</Button>
        </div>
      </div>
      <div className="fade-up d1 overflow-x-auto rounded-lg border border-plum-900/10 bg-white">
        <table className="w-full min-w-[1140px] text-sm" data-testid="staff-table">
          <thead>
            <tr className="border-b border-slate-200 font-bold text-left text-[11px] uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Mobile</th><th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Active</th><th className="px-4 py-3 text-right">Password</th>
            </tr>
          </thead>
          <tbody>
            {data.filter((u) => showInactive || u.active).map((u) => {
              const self = u.id === me.id;
              return (
                <tr key={u.id} className="border-b border-slate-100" data-testid={`staff-row-${u.email}`}>
                  <td className="px-4 py-3">
                    <Input defaultValue={u.name} onBlur={(e) => e.target.value.trim() && e.target.value !== u.name && patch(u, { name: e.target.value.trim() }, "Name updated")}
                      className="h-8 w-48" data-testid={`staff-name-${u.email}`} />
                    {u.must_change_password && <div className="mt-1 text-[11px] text-amber-700">Awaiting first sign-in</div>}
                    {u.password_locked && <div className="mt-1 text-[11px] text-red-700" data-testid={`staff-locked-${u.email}`}>Password locked · reset link sent</div>}
                  </td>
                  <td className="px-4 py-3">
                    <Input type="email" defaultValue={u.email} key={`e-${u.email}`} onBlur={(e) => { const x = e.target.value.trim().toLowerCase(); !u.main_admin && x && x !== u.email && patch(u, { email: x }, "Email updated").then((ok) => !ok && (e.target.value = u.email)); }}
                      readOnly={u.main_admin} title={u.main_admin ? "Main admin email is fixed" : undefined} className={`h-8 w-64 ${u.main_admin ? "bg-plum-50" : ""}`} data-testid={`staff-email-${u.email}`} />
                    {self && <div className="mt-1 text-[11px] text-plum-700">(you)</div>}
                  </td>
                  <td className="px-4 py-3">
                    <Input type="tel" defaultValue={u.mobile} key={`m-${u.mobile}`} placeholder="Add mobile" onBlur={(e) => { const x = e.target.value.trim(); x && x !== u.mobile && patch(u, { mobile: x }, "Mobile updated").then((ok) => !ok && (e.target.value = u.mobile || "")); }}
                      className="h-8 w-40" data-testid={`staff-mobile-${u.email}`} />
                  </td>
                  <td className="px-4 py-3">
                    <Select value={u.role} onValueChange={(v) => patch(u, { role: v }, "Role updated")} disabled={self}>
                      <SelectTrigger className="h-8 w-36" data-testid={`staff-role-${u.email}`}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="counsellor">Counsellor</SelectItem><SelectItem value="accountant">Accountant</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3">
                    <Switch checked={u.active} disabled={self} onCheckedChange={(v) => patch(u, { active: v }, v ? "Account activated" : "Account deactivated")} data-testid={`staff-active-${u.email}`} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!self && (
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" disabled={!u.active} onClick={() => setLinkFor(u)} data-testid={`staff-send-link-${u.email}`}><Mail className="mr-1.5 h-3.5 w-3.5" />Send reset link</Button>
                        <Button size="sm" variant="outline" onClick={() => reset(u)} title="Set a temporary password instead" data-testid={`staff-reset-${u.email}`}><KeyRound className="mr-1.5 h-3.5 w-3.5" />Temp password</Button>
                        {!u.active && !u.main_admin && (
                          <Button size="sm" variant="outline" onClick={() => setDelFor(u)} className="border-red-200 text-red-700 hover:bg-red-50" data-testid={`staff-delete-${u.email}`}><Trash2 className="mr-1.5 h-3.5 w-3.5" />Delete</Button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-slate-500">Counsellors can build receipts, record payments and manage follow-ups. Admins can also edit the Fee Master, Settings, counsellor reminder emails, staff accounts, and delete receipts. Accountants get view-only access to Partially Paid and Admitted students with their payments.</p>
      <AddDialog open={open} setOpen={setOpen} />
      <AlertDialog open={!!delFor} onOpenChange={(o) => !o && setDelFor(null)}>
        <AlertDialogContent data-testid="delete-staff-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {delFor?.name}'s account?</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{delFor?.email}</b> will be removed permanently and can't sign in again. Receipts they created stay in History with their name.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-staff-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="delete-staff-confirm-btn">Delete permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!linkFor} onOpenChange={(o) => !o && setLinkFor(null)}>
        <AlertDialogContent data-testid="send-link-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Send a reset link to {linkFor?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              A link valid for 24 hours goes to <b>{linkFor?.email}</b>. Their current password stops working right away and they are signed out on all devices until they set a new one.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="send-link-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={sendLink} className="bg-plum-700 hover:bg-plum-800" data-testid="send-link-confirm-btn">Send link &amp; lock password</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
